import { Components } from "@thisgorilla/primate-ds";
import styled, { useTheme } from "styled-components";
import { FlexRowCenter } from "../Styles";

type Props = {
  downvoteCount: number;
  upvoteCount: number;
  downVoteHandler: () => void;
  upVoteHandler: () => void;
  userVoted: boolean;
};
export const UpvoteDownvote = ({
  upvoteCount,
  downvoteCount,
  upVoteHandler,
  downVoteHandler,
  userVoted,
}: Props) => {
  const { Color, typography } = useTheme();

  const totalVotes = upvoteCount + downvoteCount;

  const getVotePercentage = () => {
    if (totalVotes === 0) return [0, 0];
    const upvotePercentage = (upvoteCount / totalVotes) * 100;
    const downvotePercentage = (downvoteCount / totalVotes) * 100;
    return [upvotePercentage, downvotePercentage];
  };
  const [upvotePercentage, downvotePercentage] = getVotePercentage();

  return (
    <Container
      $upvotePercentage={upvotePercentage}
      $downvotePercentage={downvotePercentage}
      $userVoted={userVoted}
    >
      <FlexRowCenter
        $gapInPx={2}
        className="up-arrow-with-count"
        onClick={upVoteHandler}
      >
        <Components.Icon
          type="Upvote"
          size="16px"
          color={
            userVoted && upvoteCount && upvoteCount >= downvoteCount
              ? Color.icon["icon-brand-bolder"].value
              : undefined
          }
        />
        <Components.Text
          style={{ tokenStyle: typography.Body.XS }}
          text={`${upvoteCount > 0 ? upvoteCount : ""}`}
          color={
            userVoted && upvoteCount && upvoteCount >= downvoteCount
              ? Color.text["text-emphasize-brand-bolder"].value
              : undefined
          }
          fontWeight="font-weight-semibold"
          lineHeight="line-height-25"
        />
      </FlexRowCenter>
      <VerticalLine
        $color={
          userVoted
            ? upvotePercentage === 0
              ? Color.border["ui-border-neutral"].value
              : Color.border["ui-border-emphasize-brand"].value
            : Color.border["ui-border-neutral"].value
        }
      />

      <TransitionContainer
        $isShow={userVoted && (upvoteCount > 0 || downvoteCount > 0)}
      >
        <Wrapper>
          <GraphContainer $percentage={upvotePercentage}>
            <UpGraph $percentage={upvotePercentage} />
            <DownGraph $percentage={downvotePercentage} />
          </GraphContainer>
        </Wrapper>
        <VerticalLine
          $color={
            downvotePercentage < upvotePercentage
              ? Color.border["ui-border-neutral"].value
              : Color.border["ui-border-emphasize-negative"].value
          }
        />
      </TransitionContainer>

      <FlexRowCenter
        $gapInPx={2}
        className="down-arrow-with-count"
        onClick={downVoteHandler}
      >
        <Components.Text
          style={{ tokenStyle: typography.Body.XS }}
          text={`${downvoteCount > 0 ? downvoteCount : ""}`}
          color={
            userVoted && downvoteCount && downvoteCount >= upvoteCount
              ? Color.text["text-emphasize-negative"].value
              : undefined
          }
          fontWeight="font-weight-semibold"
          lineHeight="line-height-25"
        />
        <Components.Icon
          type="Downvote"
          size="16px"
          color={
            userVoted && downvoteCount && downvoteCount >= upvoteCount
              ? Color.icon["icon-negative"].value
              : undefined
          }
        />
      </FlexRowCenter>
    </Container>
  );
};

type ContainerProps = {
  $upvotePercentage: number;
  $downvotePercentage: number;
  $userVoted: boolean;
};

const Container = styled.div<ContainerProps>`
  height: ${({ theme }) => theme.spacing["spacing-8"].value}rem;
  border-radius: ${({ theme }) => theme.radius["radius-xxs"].value}rem;
  border: 1px solid
    ${({ theme }) => theme.Color.border["ui-border-neutral"].value};
  display: flex;
  min-width: ${({ theme }) => theme.spacing["spacing-20"].value}rem;
  align-items: center;
  cursor: pointer;
  transition: all 300ms ease-in-out;

  .up-arrow-with-count,
  .down-arrow-with-count {
    height: 100%;
    flex: 1;
    padding: ${({ theme }) => theme.spacing["spacing-none"].value}rem
      ${({ theme }) => theme.spacing["spacing-1"].value}rem;
  }

  .up-arrow-with-count:hover {
    background: ${({ theme, $upvotePercentage, $userVoted }) =>
      $userVoted
        ? $upvotePercentage > 0
          ? theme.Color.elevation.surface["surface-primary-emphasize-hover"]
              .value
          : theme.Color.elevation.surface["surface-primary-hover"].value
        : theme.Color.elevation.surface["surface-primary-hover"].value};
  }

  .down-arrow-with-count:hover {
    background: ${({ theme, $downvotePercentage, $userVoted }) =>
      $userVoted
        ? $downvotePercentage > 0
          ? theme.Color.border.negative.subtlest["default-hover"].value
          : theme.Color.elevation.surface["surface-primary-hover"].value
        : theme.Color.elevation.surface["surface-primary-hover"].value};
  }
`;

const GraphContainer = styled.div<{ $percentage: number }>`
  display: flex;
  align-items: center;
  position: relative;

  &:after {
    content: "";
    position: absolute;
    left: ${({ $percentage }) =>
      `calc(${$percentage > 0 ? "-0.5px" : "0px"} + ${$percentage}%)`};
    top: -9px;
    height: ${({ theme }) => theme.spacing["spacing-5"].value}rem;
    width: 1px;
    background: ${({ $percentage, theme }) =>
      $percentage === 50
        ? `linear-gradient(270deg,  ${theme.Color.icon.negative.bolder.default.value} 0%, ${theme.Color.icon.brand.bold.default.value} 100%)`
        : $percentage > 50
        ? theme.Color.icon.brand.bold.default.value
        : theme.Color.icon.negative.bolder.default.value};
    transition: all 300ms ease-in-out;
  }
  &:before {
    content: "";
    position: absolute;
    left: ${({ $percentage }) => `calc(-1.5px + ${$percentage}%)`};
    top: -12px;
    z-index: 1;
    height: ${({ theme }) => theme.spacing["spacing-1"].value}rem;
    width: ${({ theme }) => theme.spacing["spacing-1"].value}rem;
    background: ${({ $percentage, theme }) =>
      $percentage === 50
        ? `linear-gradient(90deg, ${theme.Color.background.brand.default.value} 0%, #B40A32 100%)`
        : $percentage > 50
        ? theme.Color.background.brand.default.value
        : theme.Color.icon.negative.default.value};
    border: 0.5px solid white;
    border-radius: ${({ theme }) => theme.radius["radius-full-round"].value}rem;
    box-sizing: border-box;
    box-shadow: drop-shadow(1px 1px 7px #d2d7db);
    transition: all 300ms ease-in-out;
  }
`;

const Wrapper = styled.div`
  width: 67px;
  padding: 7px 3px;
`;

const UpGraph = styled.div<{ $percentage: number }>`
  height: ${({ theme }) => theme.spacing["spacing-1"].value}rem;
  width: ${({ $percentage }) => $percentage}%;
  background: linear-gradient(
    270deg,
    ${({ theme }) => theme.Color.background.brand.default.value} 27.5%,
    #11578c 137.5%
  );
  opacity: 0.5;
  transition: all 300ms ease-in-out;
`;
const DownGraph = styled.div<{ $percentage: number }>`
  height: ${({ theme }) => theme.spacing["spacing-1"].value}rem;
  width: ${({ $percentage }) => $percentage}%;
  background: linear-gradient(90deg, #f21e81 0%, #8c114b 157.14%);
  opacity: 0.5;
  transition: all 300ms ease-in-out;
`;
const VerticalLine = styled.span<{ $color: string }>`
  height: 30px;
  width: 0.5px;
  background: ${({ $color }) => $color};
`;

const TransitionContainer = styled.div<{ $isShow: boolean }>`
  display: flex;
  align-items: center;
  opacity: ${({ $isShow }) => ($isShow ? "1" : "0")};
  width: ${({ $isShow }) => ($isShow ? "100%" : "0px")};
  height: 100%;
  transition: width 300ms ease-in-out, opacity 0ms 300ms ease-in-out;
`;
